import { ContentConverter } from '@/utils/contentConverter';
import { geoUtils } from '@/utils/geoUtils';

export interface MediumConnection {
  id: string;
  settings: {
    token: string;
    publicationId?: string;
    format?: 'markdown' | 'html';
  };
}

export interface MediumPost {
  title: string;
  content: string;
  tags?: string[];
  excerpt?: string;
  canonicalUrl?: string; // GEO: Support canonical link
}

export interface PublishingProgress {
  connectionId: string;
  status: 'in_progress' | 'success' | 'error';
  message: string;
  externalUrl?: string;
}

export class MediumPublishingService {
  async publishToMedium(
    connection: MediumConnection,
    post: MediumPost,
    onProgress?: (progress: PublishingProgress) => void
  ): Promise<void> {
    const progressCallback = onProgress || (() => { });

    try {
      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Converting content format...'
      });

      // Convert content based on connection format setting (Medium prefers markdown)
      const targetFormat = connection.settings.format || 'markdown';

      // GEO: Inject Visible Summary for Medium (since it strips scripts)
      let contentToConvert = post.content;
      if (post.excerpt) {
        const visibleSummary = geoUtils.generateMarkdownSummary(post.excerpt);
        contentToConvert = visibleSummary + contentToConvert;
      }

      const convertedContent = await ContentConverter.prepareForPublishing(
        contentToConvert,
        targetFormat,
        {
          title: post.title,
          tags: post.tags,
          excerpt: post.excerpt
        }
      );

      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Getting user information...'
      });

      // Get user info first
      const userResponse = await fetch('https://api.medium.com/v1/me', {
        headers: {
          'Authorization': `Bearer ${connection.settings.token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!userResponse.ok) {
        throw new Error(`Failed to get user info: ${userResponse.statusText}`);
      }

      const userData = await userResponse.json();
      const userId = userData.data.id;

      progressCallback({
        connectionId: connection.id,
        status: 'in_progress',
        message: 'Publishing to Medium...'
      });

      // Prepare the post data
      const postData = {
        title: post.title,
        contentFormat: targetFormat,
        content: convertedContent,
        tags: post.tags || [],
        publishStatus: 'public',
        canonicalUrl: post.canonicalUrl
      };

      // Determine the endpoint (user or publication)
      let endpoint: string;
      if (connection.settings.publicationId) {
        endpoint = `https://api.medium.com/v1/publications/${connection.settings.publicationId}/posts`;
      } else {
        endpoint = `https://api.medium.com/v1/users/${userId}/posts`;
      }

      // Make the publish request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${connection.settings.token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Medium API error: ${errorData.errors?.[0]?.message || response.statusText}`);
      }

      const responseData = await response.json();

      progressCallback({
        connectionId: connection.id,
        status: 'success',
        message: 'Successfully published to Medium',
        externalUrl: responseData.data.url
      });

    } catch (error) {
      progressCallback({
        connectionId: connection.id,
        status: 'error',
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      });
      throw error;
    }
  }
}

export const mediumPublishingService = new MediumPublishingService();
